import { type CSSProperties, Suspense, useEffect, useRef } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { MODELS, useModel } from "../../../api";
import { Spinner } from "../../../design-system/utils/Spinner/Spinner.jsx";
import { responsiveIFrameContainer } from "../../../design-system/components/Video/Video.jsx";
import ErrorBoundary from "../../../lib/ErrorBoundary";

type VideoJsOptions = {
  autoplay: boolean;
  controls: boolean;
  sources: { src: string; type: string }[];
};

/**
 * A video.js player over a `<video-js>` element it creates itself, so React
 * never touches the DOM video.js manages. Created once on mount, disposed on
 * unmount.
 */
const VideoJS = ({ options }: { options: VideoJsOptions }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<ReturnType<typeof videojs> | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || playerRef.current) return;
    const videoElement = document.createElement("video-js");
    videoElement.className = "vjs-fill"; // Makes video.js fill the container
    container.appendChild(videoElement);

    playerRef.current = videojs(videoElement, {
      ...optionsRef.current,
      responsive: true,
      fluid: true,
      aspectRatio: "16:9",
      fill: true,
    });
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <div style={responsiveIFrameContainer(9 / 16) as CSSProperties}>
      <div
        ref={containerRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
};

/**
 * Loads the PeerTube streaming URL and renders the player. Suspends while the
 * URL loads; throws if the video is unavailable (e.g. still transcoding), which
 * the wrapping ErrorBoundary turns into an inline notice.
 */
const PeertubeVideoPlayer = ({ data }: { data: { id: string } }) => {
  const { videoUrl } = useModel(MODELS.PEERTUBE_VIDEO_FILE_URL(data.id));

  return (
    <VideoJS
      options={{
        autoplay: false,
        controls: true,
        sources: [{ src: videoUrl, type: "application/x-mpegURL" }],
      }}
    />
  );
};

/**
 * Embeds a PeerTube video. It is isolated in its own Suspense + ErrorBoundary
 * so a still-processing / unavailable video degrades to an inline notice
 * instead of bubbling up and blanking the surrounding content.
 */
const PeertubeVideoEmbed = ({ data }: { data: { id: string } }) => (
  <ErrorBoundary
    key={data.id}
    fallback={
      <p>
        Dieses Video ist derzeit nicht verfügbar (evtl. noch in Bearbeitung).
      </p>
    }
  >
    <Suspense fallback={<Spinner />}>
      <PeertubeVideoPlayer data={data} />
    </Suspense>
  </ErrorBoundary>
);

export default PeertubeVideoEmbed;
